import React, { Fragment } from "react";
import _ from "lodash";
import styled from "styled-components";
import moment from "moment";
import { withStyles } from "@material-ui/core/styles";
import { ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Grid } from "@material-ui/core";
import { ExpandMore, Edit, Delete } from "@material-ui/icons";
import numberFormatter from "utils/numberFormatter";
import unitAssetTypes from "constants/unitAssetTypes";
import { ASSET_CLASS, ASSET_TYPE, ZONING, METROPOLITAN_STATISTICAL_AREA, STATES, REGION, MARKET_VALUE_METHODOLOGY } from "constants/configItemParents";

const DealPropertySummary = styled.div`
  & {
    display:flex;
    flex-flow:row;
    align-items:center;
    width:100%;
    font-size:17px;
  }
`;

const StyledAsOfDate = styled(Grid)`
  && {
    font-size: 15px;
    color: #333333;
  }
`;

const Spacer = styled.div`
  flex-grow:1;
`;
const ActionContainer = styled.div`
  position:relative;  
  z-index:2;
`;
const SeparatedContainer = styled.div`
  display:flex;
  flex-flow:row;
  justify-content: space-between;
  margin: 0 10px;

`;
const LabelGrid = styled.div`
  font-weight:bold;
  font-size:17px;  
  width:50%;
  padding-right: 8px;
`;
const TextValueGrid = styled.div`    
  font-size:17px;
  width:50%;
`;
const Label = ({ children }) => (
  <LabelGrid>
    {children}
  </LabelGrid>
);
const TextValue = ({ children, internalID }) => (
  <TextValueGrid name={internalID}>
    {children}
  </TextValueGrid>
);

const styles = theme => ({
  editIconRoot: {

    color: "#cccccc",
    "&:hover": {
      color: "#2196F3"
    },
    position: "absolute",
    top: "-12px",
    left: "36px"
  },
  deleteIconRoot: {

    color: "#cccccc",
    "&:hover": {
      color: "#2196F3"
    },
    position: "absolute",
    top: "-12px",
    left: "68px"
  },
  expandedPanel: {
    margin: 0
  },
  columnarDetails: {
    flexFlow: "column",
    justifyContent: "space-between"
  }
});

const TextAttribute = ({ label, value, isAlwaysVisible }) => {
  if (!value && !isAlwaysVisible) {
    return null;
  }

  return (
    <SeparatedContainer>
      <Label>{label}</Label>
      <TextValue internalID={`REPvalueFor${label}~${value}`}>{value || "-"}</TextValue>
    </SeparatedContainer>
  );
};
const AverageUnitSF = ({ squareFeet, units }) => {
  if (!squareFeet || !units) {
    return null;
  }
  const result = squareFeet / units;
  const formatted = numberFormatter.formatDecimal(result, 0);

  return (
    <SeparatedContainer>
      <Label>Average Unit SF</Label>
      <TextValue internalID={`REPvalueFor${"Average Unit SF"}~${formatted}`}>{formatted}</TextValue>
    </SeparatedContainer>
  );
};
const Density = ({ landSize, units }) => {
  if (!landSize || !units) {
    return null;
  }
  const result = units / landSize;
  const formatted = numberFormatter.formatDecimal(result, 1);

  return (
    <SeparatedContainer>
      <Label>Density</Label>
      <TextValue internalID={`REPvalueFor${"Density"}~${formatted}`}>{formatted}</TextValue>
    </SeparatedContainer>
  );
};
const DateAttribute = ({ label, value, isAlwaysVisible }) => {
  if (!value && !isAlwaysVisible) {
    return null;
  }

  return (
    <SeparatedContainer>
      <Label>{label}</Label>
      <TextValue internalID={`REPvalueFor${label}~${moment.utc(value).format("M/D/YYYY")}`}>{moment.utc(value).format("M/D/YYYY")}</TextValue>
    </SeparatedContainer>
  );
};
const NumberAttribute = ({ label, value, isAlwaysVisible }) => {
  if (!value && !isAlwaysVisible) {
    return null;
  }

  return (
    <SeparatedContainer>
      <Label>{label}</Label>
      <TextValue internalID={`REPvalueFor${label}~${numberFormatter.formatWholeNumber(value, "-")}`}>{numberFormatter.formatWholeNumber(value, "-")}</TextValue>
    </SeparatedContainer>
  );
};
const MoneyAttribute = ({ label, value, isAlwaysVisible }) => {
  if (!value && !isAlwaysVisible) {
    return null;
  }

  return (
    <SeparatedContainer>
      <Label>{label}</Label>
      <TextValue internalID={`REPvalueFor${label}~${numberFormatter.formatMoney(value, 2, "-")}`}>{numberFormatter.formatMoney(value, 2, "-")}</TextValue>
    </SeparatedContainer>
  );
};
const RateAttribute = ({ label, value, isAlwaysVisible, shouldNotMultiply }) => {
  if (!value && !isAlwaysVisible) {
    return null;
  }

  return (
    <SeparatedContainer>
      <Label>{label}</Label>
      <TextValue internalID={`REPvalueFor${label}~${numberFormatter.formatRate(value, 0, "-", shouldNotMultiply)}`}>
        {numberFormatter.formatRate(value, 0, "-", shouldNotMultiply)}
      </TextValue>
    </SeparatedContainer>
  );
};
const getConfigItemValue = (propertyConfigItems, parentTypeId, id) => {
  if (!propertyConfigItems) {
    return null;
  }
  if (!propertyConfigItems[parentTypeId]) {
    return null;
  }
  const found = _.find(propertyConfigItems[parentTypeId], { id });
  if (!found) {
    return null;
  }

  return found.name;
};
const shouldShowUnits = assetTypeId => {
  if (!assetTypeId) {
    return false;
  }

  return unitAssetTypes.hasOwnProperty(assetTypeId);
};

const formatLoanName = loan => {
  const lenderCompany = _.get(loan, "lenderCompany.name") ? _.get(loan, "lenderCompany.name") : "Lender Selection Needed";
  const loanSeniority = _.get(loan, "loanSeniority.value") ? ` - ${_.get(loan, "loanSeniority.value")}` : "";
  const formattedClosingDate = _.get(loan, "closingDate") ? ` - ${moment.utc(_.get(loan, "closingDate")).format("YYYY")}` : "";

  return `${lenderCompany}${loanSeniority}${formattedClosingDate}`;
};

const ExpandableDealProperty = ({ property, isExpanded, classes, onClickExpand, onEditProperty, onDeleteProperty, propertyConfigItems, isViewOnlyMode, guestPermissions, authentication }) => {
  const assetTypeValue = getConfigItemValue(propertyConfigItems, ASSET_TYPE, property.assetTypeId);
  const assetClassValue = getConfigItemValue(propertyConfigItems, ASSET_CLASS, property.assetClassId);
  const msaValue = getConfigItemValue(propertyConfigItems, METROPOLITAN_STATISTICAL_AREA, property.metropolitanStatisticalAreaId);
  const stateValue = getConfigItemValue(propertyConfigItems, STATES, property.stateId);
  const regionValue = getConfigItemValue(propertyConfigItems, REGION, property.regionId);
  const zoningValue = getConfigItemValue(propertyConfigItems, ZONING, property.zoningId);
  const marketValueMethodologyValue = getConfigItemValue(propertyConfigItems, MARKET_VALUE_METHODOLOGY, property.marketValueMethodologyId);
  const isSold = Boolean(property.actualExitDate);

  return (
    <ExpansionPanel classes={{ expanded: classes.expandedPanel }}>
      <ExpansionPanelSummary expandIcon={<ExpandMore />} onChange={onClickExpand} style={{ padding: 0 }}>
        <DealPropertySummary>
          <>
            <Grid container direction="row">
              <Grid style={{ color: isSold ? "#888888" : "" }} item sm={12}>
                <div id={`realEstatePropertyName${property.id}`}>{property.address}</div>
              </Grid>
              <StyledAsOfDate style={{ color: isSold ? "#888888" : "" }} item sm={12}>
                {getPropertySubtext(property, isSold)}
              </StyledAsOfDate>
            </Grid>
          </>
          <Spacer />
          { !isViewOnlyMode &&
            <ActionContainer>
              {(!authentication.isGuest || (authentication.isGuest && guestPermissions.canEdit)) && <Edit name="editREPIcon" onClick={onEditProperty} classes={{ root: classes.editIconRoot }} />}
              {!authentication.isGuest && <Delete name="deleteREPIcon" onClick={onDeleteProperty} classes={{ root: classes.deleteIconRoot }} />}
            </ActionContainer>}
        </DealPropertySummary>
      </ExpansionPanelSummary>
      <ExpansionPanelDetails classes={{ root: classes.columnarDetails }} style={{ padding: 0 }}>
        <TextAttribute label="Property Name" value={property.name} isAlwaysVisible />
        <TextAttribute label="Asset Type" value={assetTypeValue} isAlwaysVisible />
        <TextAttribute label="Class" value={assetClassValue} isAlwaysVisible />
        <div><hr /></div>
        <MoneyAttribute label="NOI (Lender)" value={property.noi} isAlwaysVisible />
        <MoneyAttribute label="NOI (Actual)" value={property.noiActual} isAlwaysVisible />
        <RateAttribute label="Occupancy" value={property.occupancy} isAlwaysVisible shouldNotMultiply />
        <DateAttribute label="As of" value={property.noiAsOfDate} />
        <div><hr /></div>
        <MoneyAttribute label="Appraised Value" value={property.appraisedValue} isAlwaysVisible />
        <DateAttribute label="As of" value={property.appraisedValueAsOfDate} />
        <div><hr /></div>
        <MoneyAttribute label="Market Value" value={property.marketValue} isAlwaysVisible />
        <DateAttribute label="As of" value={property.marketValueAsOfDate} />
        <TextAttribute label="Methodology" value={marketValueMethodologyValue} />
        <div><hr /></div>
        <NumberAttribute label="Square Feet" value={property.squareFeet} isAlwaysVisible />
        <NumberAttribute label="Land Size" value={property.landSize} />
        {shouldShowUnits(property.assetTypeId) && <NumberAttribute label="Units" value={property.units} isAlwaysVisible />}
        <AverageUnitSF units={property.units} squareFeet={property.squareFeet} />
        <Density units={property.units} landSize={property.landSize} />
        <TextAttribute label="MSA" value={msaValue} />
        <TextAttribute label="Street Address" value={property.address} />
        <TextAttribute label="City" value={property.city} />
        <TextAttribute label="State" value={stateValue} />
        <TextAttribute label="Zip Code" value={property.zip} />
        <TextAttribute label="Year Built" value={property.yearBuilt} />
        <TextAttribute label="Year Renovated" value={property.yearRenovated} />
        <TextAttribute label="Zoning" value={zoningValue} />
        <TextAttribute label="Region" value={regionValue} />
        <DateAttribute label="Purchase Date" value={property.purchaseDate} />
        <MoneyAttribute label="Purchase Price" value={property.purchasePrice} />
        <DateAttribute label="Projected Exit Date" value={property.projectedExitDate} />
        <DateAttribute label="Actual Exit Date" value={property.actualExitDate} />
        <MoneyAttribute label="Sales Price" value={property.salesPrice} />
        <MoneyAttribute label="Price Per Unit" value={property.pricePerUnit} />
        <MoneyAttribute label="Price Per Square Foot" value={property.pricePerSquareFoot} />
        <div><hr /></div>
        {property.propertyLoanBridge.map(propertyLoan => (
          <RateAttribute label={formatLoanName(propertyLoan.loan)} value={propertyLoan.allocationPercent} isAlwaysVisible />
        ))}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  );
};

const getPropertySubtext = (property, isSold) => {
  if (isSold) {
    return <span>Property Sold</span>;
  }

  return <span>
    Property Data as of
    { property.noiAsOfDate ? `${moment.utc(property.noiAsOfDate).format("M/D/YYYY")}` : ""}
  </span>;
};

export default withStyles(styles)(ExpandableDealProperty);
